import React from 'react';

/**
 * BidHealthBadge - Primary visual anchor for instant bid assessment
 * 
 * Color Logic:
 * - 80-100: Green (Strong Position)
 * - 60-79: Amber (Manageable Risk)
 * - 40-59: Orange (Margin Pressure)
 * - <40: Red (High Risk)
 */
export const BidHealthBadge = ({ 
  score, 
  level, 
  color, 
  interpretation,
  size = 'large' // 'large', 'medium', 'small'
}) => {
  const colorMap = {
    green: {
      bg: 'bg-green-500',
      text: 'text-white',
      border: 'border-green-600',
      glow: 'shadow-green-500/20'
    },
    amber: {
      bg: 'bg-amber-500',
      text: 'text-white',
      border: 'border-amber-600',
      glow: 'shadow-amber-500/20'
    },
    orange: {
      bg: 'bg-orange-500',
      text: 'text-white',
      border: 'border-orange-600',
      glow: 'shadow-orange-500/20'
    },
    red: {
      bg: 'bg-red-500',
      text: 'text-white',
      border: 'border-red-600',
      glow: 'shadow-red-500/20'
    }
  };

  const colors = colorMap[color] || colorMap.amber;

  const sizeClasses = {
    large: 'px-6 py-4',
    medium: 'px-4 py-3',
    small: 'px-3 py-2'
  };

  const scoreClasses = {
    large: 'text-4xl',
    medium: 'text-2xl',
    small: 'text-xl'
  };

  const labelClasses = {
    large: 'text-sm',
    medium: 'text-xs',
    small: 'text-xs'
  };

  return (
    <div className="flex flex-col items-end" data-testid="bid-health-badge">
      <div 
        className={`
          ${colors.bg} ${colors.text} ${sizeClasses[size]}
          rounded-sm shadow-lg ${colors.glow}
          font-mono tracking-tight
        `}
      >
        <div className={`${labelClasses[size]} opacity-90 uppercase tracking-wider mb-1`}>
          BID HEALTH
        </div>
        <div className={`${scoreClasses[size]} font-black`}>
          {score} <span className="text-lg font-normal opacity-80">/ 100</span>
        </div>
      </div>
      {interpretation && size !== 'small' && (
        <div className="mt-2 text-right max-w-xs">
          <p className="text-xs text-slate-500 leading-relaxed">
            {interpretation}
          </p>
        </div>
      )}
    </div>
  );
};


/**
 * CompetitivePositionBar - Visual positioning system
 * Replaces numeric win probability with intuitive band display
 */
export const CompetitivePositionBar = ({
  position,
  positionPercent,
  zone,
  zoneLabel,
  deviationText
}) => {
  // Position: -100 (too aggressive) to +100 (overpriced), 0 = balanced
  // positionPercent: 0-100 for CSS positioning
  
  const zoneColors = {
    aggressive: 'text-red-600',
    competitive: 'text-amber-600',
    balanced: 'text-green-600',
    conservative: 'text-amber-600',
    overpriced: 'text-red-600',
    below_median: 'text-amber-600',
    within_band: 'text-green-600',
    above_median: 'text-slate-600',
    unknown: 'text-slate-400'
  };

  return (
    <div className="w-full" data-testid="competitive-position-bar">
      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-500 uppercase tracking-wider mb-2">
        <span>Too Aggressive</span>
        <span>Balanced</span>
        <span>Overpriced</span>
      </div>
      
      {/* Track */}
      <div className="relative h-3 rounded-full overflow-hidden bg-gradient-to-r from-red-400 via-amber-400 via-green-400 via-amber-400 to-red-400">
        {/* Center marker (balanced zone) */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/50 transform -translate-x-1/2" />
        
        {/* Position marker */}
        <div 
          className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 transition-all duration-300"
          style={{ left: `${positionPercent}%` }}
        >
          <div className="w-5 h-5 bg-slate-900 border-2 border-white rounded-full shadow-lg" />
        </div>
      </div>
      
      {/* Position info */}
      <div className="mt-4 text-center">
        <div className={`font-semibold ${zoneColors[zone] || 'text-slate-700'}`}>
          Competitive Position: {zoneLabel}
        </div>
        {deviationText && (
          <p className="text-sm text-slate-500 mt-1">
            {deviationText}
          </p>
        )}
      </div>
    </div>
  );
};


/**
 * HealthIndicator - Compact inline health display
 */
export const HealthIndicator = ({ score, color }) => {
  const colorMap = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };

  return (
    <div className="flex items-center gap-2" data-testid="health-indicator">
      <div className={`w-2 h-2 rounded-full ${colorMap[color] || colorMap.amber}`} />
      <span className="font-mono font-semibold text-sm">{score}</span>
    </div>
  );
};


export default BidHealthBadge;
